<app-banner [links]="bannnerLinks"></app-banner>

<div class="container mx-auto px-4 py-8">
    @if (loading) {
    <div class="flex justify-center items-center min-h-[400px]">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
    } @else if (error) {
    <div class="text-red-500 text-center">{{ error }}</div>
    } @else if (doctor) {
    <!-- Doctor Info Header -->
    <div class="bg-white rounded-lg p-4 mb-6 flex items-start gap-4">
        <!-- Avatar -->
        <div class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
            @if (doctor.avatar) {
            <img [src]="doctor.avatar" [alt]="doctor.firstname" class="w-full h-full object-cover rounded-full">
            } @else {
            <lucide-icon [img]="UserIcon" color="gray" [size]="18"></lucide-icon>
            }
        </div>

        <!-- Doctor Info -->
        <div class="flex-1">
            <h4 class="text-[14px] font-medium text-gray-900">
                {{ doctor.title?.name ?? '' }} {{ doctor?.firstname }} {{ doctor?.lastname }}
            </h4>
            @if (doctor?.doctorSpecialisations?.length > 0) {
            <div class="flex flex-wrap gap-1.5 mt-1.5">
                @for (spec of doctor.specialisations.slice(0, 3); track spec._id) {
                <span class="bg-primary/10 text-primary text-[11px] font-medium px-2 py-0.5 rounded-full">
                    {{ spec?.name ?? spec }}
                </span>
                }
                @if (doctor.doctorSpecialisations.length > 3) {
                <span class="text-primary text-[11px] font-medium">
                    +{{ doctor.doctorSpecialisations.length - 3 }}
                </span>
                }
            </div>
            }

            <!-- Add Languages section -->
            @if (doctor?.languages?.length > 0) {
            <div class="flex items-center gap-2 mt-1">
                <span class="text-[13px] text-gray-600 font-medium">Langues:</span>
                <p class="text-[13px] text-gray-600">
                    @for (lang of doctor.languages; track $index; let last = $last) {
                    {{ lang.name }}@if (!last) {, }
                    }
                </p>
            </div>
            }

            <!-- Contact Info -->
            <div class="mt-3 space-y-1.5">
                @if (doctor.coordonate?.email) {
                <div class="flex items-center gap-2 text-gray-600">
                    <lucide-icon [img]="MailIcon" color="gray" [size]="14"></lucide-icon>
                    <span class="text-[13px]">{{ doctor.coordonate.email }}</span>
                </div>
                }
                @if (doctor.coordonate?.mobileNumber || doctor.coordonate?.fixNumber) {
                <div class="flex items-center gap-2 text-gray-600">
                    <lucide-icon [img]="PhoneIcon" color="gray" [size]="14"></lucide-icon>
                    <span class="text-[13px]">
                        @if (doctor.coordonate?.mobileNumber) {
                        {{ doctor.coordonate.mobileNumber }}
                        }
                        @if (doctor.coordonate?.mobileNumber && doctor.coordonate?.fixNumber) {
                        &nbsp;/&nbsp;
                        }
                        @if (doctor.coordonate?.fixNumber) {
                        {{ doctor.coordonate.fixNumber }}
                        }
                    </span>
                </div>
                }
            </div>
        </div>
    </div>

    <!-- Details Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <!-- Left Column - Details -->
        <div class="lg:col-span-2 space-y-6">
            <!-- Specialisations -->
            <div class="bg-white rounded-lg p-4">
                <h5 class="text-[13px] font-medium text-primary mb-3">
                    Spécialisations
                </h5>
                @if (doctor.specialisations?.length) {
                <div class="flex flex-wrap gap-2">
                    @for (spec of doctor.specialisations; track $index) {
                    <span
                        class="bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/20 hover:bg-primary/20 transition-colors">
                        {{ spec.name || spec }}
                    </span>
                    }
                </div>
                } @else if (doctor.doctorSpecialisations?.length) {
                <div class="flex flex-wrap gap-2">
                    @for (spec of doctor.doctorSpecialisations; track spec._id) {
                    <span
                        class="bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/20 hover:bg-primary/20 transition-colors">
                        {{ spec.specialisation?.name || spec }}
                    </span>
                    }
                </div>
                } @else {
                <div class="text-gray-400 text-[13px] italic">
                    Aucune spécialisation disponible
                </div>
                }
            </div>

            <!-- Practice Locations -->
            <div class="bg-white rounded-lg p-4">
                <div class="flex items-center gap-2 mb-3">
                    <lucide-icon [img]="BuildingIcon" color="#2B3C8B" [size]="14"></lucide-icon>
                    <h5 class="text-[13px] font-medium text-primary">Lieux d'exercice</h5>
                </div>
                @if (doctor.structures?.length) {
                <div class="space-y-1.5">
                    @for (structure of doctor.structures; track structure.id) {
                    <div class="text-gray-600 text-[13px]">{{ structure.name }}</div>
                    }
                </div>
                }
            </div>

            <!-- Insurance & Payment -->
            <div class="bg-white rounded-lg p-6">
                <div class="grid grid-cols-2 gap-6">
                    <!-- Insurers -->
                    <div>
                        <h5 class="text-[13px] font-medium text-primary mb-3">Organismes de remboursement acceptés</h5>
                        @if (doctor.insurers?.length) {
                        <div class="space-y-1.5">
                            @for (insurer of doctor.insurers; track insurer.id) {
                            <div class="text-gray-600 text-[13px]">{{ insurer.name }}</div>
                            }
                        </div>
                        } @else {
                        <div class="text-gray-400 text-[13px] italic">Aucune information disponible</div>
                        }
                    </div>

                    <!-- Payment Methods -->
                    <div>
                        <h5 class="text-[13px] font-medium text-primary mb-3">Moyens de paiement acceptés</h5>
                        @if (doctor.paymentTypes?.length) {
                        <div class="space-y-1.5">
                            @for (payment of doctor.paymentTypes; track payment.id) {
                            <div class="text-gray-600 text-[13px]">{{ payment.name }}</div>
                            }
                        </div>
                        } @else {
                        <div class="text-gray-400 text-[13px] italic">Aucune information disponible</div>
                        }
                    </div>

                    <!-- MedicalActs -->
                    <div>
                        <h5 class="text-[13px] font-medium text-primary mb-3">Actes médicaux pratiqués</h5>
                        @if (doctor.doctorMedicalActs?.length) {
                        <div class="space-y-1.5">
                            @for (act of doctor.doctorMedicalActs; track act.id) {
                            <div class="text-gray-600 text-[13px]">{{ act.name }}</div>
                            }
                        </div>
                        } @else {
                        <div class="text-gray-400 text-[13px] italic">Aucune information disponible</div>
                        }
                    </div>

                    <!-- MedicalAppointmentReasons -->
                    <div>
                        <h5 class="text-[13px] font-medium text-primary mb-3">Motifs de consultation</h5>
                        @if (doctor.doctorAppointmentReasons?.length) {
                        <div class="space-y-1.5">
                            @for (reason of doctor.doctorAppointmentReasons; track reason.eyoneExternalId) {
                            <div class="text-gray-600 text-[13px]">{{ reason.label }}</div>
                            }
                        </div>
                        } @else {
                        <div class="text-gray-400 text-[13px] italic">Aucune information disponible</div>
                        }
                    </div>
                </div>
            </div>
            <div class="bg-white rounded-lg p-6">
                <h5 class="text-md font-medium text-primary mb-3">Adresse et localisation</h5>
                @if (doctor.coordonate?.globalAddressField) {
                <p class="text-gray-600 mb-4">{{ doctor.coordonate.globalAddressField }}</p>
                }
                <div class="h-[300px] rounded-lg overflow-hidden">
                    <app-safe-maps [latitude]="doctor.coordonate?.latitude ?? 0"
                        [longitude]="doctor.coordonate?.longitude ?? 0" [zoom]="15">
                    </app-safe-maps>
                </div>
            </div>
        </div>

        <!-- Right Column - Créneaux / multi-structures -->
        <div class="lg:col-span-3 h-fit bg-white rounded-lg p-4">
            <div class="h-full">
                <app-doctor-structure-slots [doctor]="doctor"></app-doctor-structure-slots>
            </div>
        </div>
    </div>
    }
</div>